import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
} from "@mui/material";
import { AddRounded, DeleteRounded } from "@mui/icons-material";
import { currencyAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";

const CurrencyPage = () => {
  const { canCreateModule, canDeleteModule } = useAuth();
  const [currencies, setCurrencies] = useState([]);
  const [open, setOpen] = useState(false);
  const [alert, setAlert] = useState({ open: false, msg: "", type: "success" });

  const [form, setForm] = useState({
    code: "",
    name: "",
    symbol: "",
  });

  const showAlert = (msg, type = "success") => {
    setAlert({ open: true, msg, type });
    setTimeout(() => setAlert({ open: false, msg: "", type: "success" }), 3000);
  };

  const fetchCurrencies = async () => {
    try {
      const res = await currencyAPI.getCurrencies();
      setCurrencies(res.data);
    } catch (err) {
      showAlert("Error fetching currencies", "error");
    }
  };

  useEffect(() => {
    fetchCurrencies();
  }, []);

  const onSubmit = async () => {
    if (!form.code || !form.name) {
      showAlert("Code & Name are required", "error");
      return;
    }

    try {
      await currencyAPI.createCurrency(form);
      showAlert("Currency added successfully");
      fetchCurrencies();
      setOpen(false);
      setForm({ code: "", name: "", symbol: "" });
    } catch {
      showAlert("Failed to add currency", "error");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this currency?")) return;

    try {
      await currencyAPI.deleteCurrency(id);
      showAlert("Deleted successfully");
      fetchCurrencies();
    } catch {
      showAlert("Delete failed", "error");
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {alert.open && (
        <Alert severity={alert.type} sx={{ mb: 2 }}>
          {alert.msg}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="h5" fontWeight={600}>
            Currency Master
          </Typography>

          {canCreateModule('currency') && (
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => setOpen(true)}
            >
              Add Currency
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Currency Table */}
      <Card>
        <CardContent>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Symbol</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currencies.map((c) => (
                <TableRow key={c.id} hover>
                  <TableCell>{c.code}</TableCell>
                  <TableCell>{c.name}</TableCell>
                  <TableCell>{c.symbol}</TableCell>
                  <TableCell align="center">
                    {canDeleteModule('currency') && (
                      <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => onDelete(c.id)}>
                          <DeleteRounded />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {currencies.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No currencies added yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add Currency Modal */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Currency</DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Currency Code (INR / USD)"
                name="code"
                fullWidth
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Name (Indian Rupee / US Dollar)"
                name="name"
                fullWidth
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Symbol (₹ / $ / €)"
                name="symbol"
                fullWidth
                value={form.symbol}
                onChange={(e) => setForm({ ...form, symbol: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onSubmit}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CurrencyPage;
